import { notifyProgress } from '@billy/design-system'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { downloadStatusToTKeys } from '../../../utils/export/downloadStatusToTKeys'
import { sanitizeFileName } from '../../../utils/export/sanitizeFileName'

const MINIMUM_COLUMN_WIDTH = 10
/* xlsx's wch === "how many _digit_ characters (0–9) fit in this column" at the default font */
/* but we have not only digits but also letters and other characters, so it is huge */
/* scaling by 0.85 helps to eradicate the difference */
/* min width and padding help to keep it consistent for digits as well */
const CHAR_WIDTH_SCALE = 0.85
const COLUMN_WIDTH_PADDING = 2

interface UseXlsxExportProps {
  spreadSheetData: (string | number)[][]
  fileName: string
  notificationKey: NotificationKeys
}

export const useXlsxExport = ({ spreadSheetData, fileName, notificationKey }: UseXlsxExportProps) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()

  const organizationName = organization?.name || ''
  const [isLoading, setIsLoading] = useState(false)

  const exportAsXlsx = useCallback(async () => {
    try {
      setIsLoading(true)
      const { utils, writeFileXLSX } = await import('xlsx')

      const worksheet = utils.aoa_to_sheet(spreadSheetData)

      if (spreadSheetData?.length > 0) {
        const maxColumns = spreadSheetData.reduce((max, row) => Math.max(max, row.length), 0)

        const columnProperties = []

        for (let columnIndex = 0; columnIndex < maxColumns; columnIndex++) {
          const maxTextLength = spreadSheetData.reduce((max, row) => {
            const cellValue = row[columnIndex]?.toString() ?? ''
            return Math.max(max, cellValue.length)
          }, MINIMUM_COLUMN_WIDTH)

          columnProperties.push({ wch: Math.ceil(maxTextLength * CHAR_WIDTH_SCALE) + COLUMN_WIDTH_PADDING })
        }

        worksheet['!cols'] = columnProperties
      }

      const workbook = utils.book_new()
      const finalFileName = `${fileName}-${sanitizeFileName(organizationName)}.xlsx`

      utils.book_append_sheet(workbook, worksheet, 'Raport')
      writeFileXLSX(workbook, finalFileName)

      const successTKeys = downloadStatusToTKeys.success
      notifyProgress({
        id: notificationKey,
        title: t(successTKeys.title),
        description: t(successTKeys.description),
        variant: 'success',
        progress: 100,
        closable: true,
      })
      setIsLoading(false)
    } catch {
      const errorTKeys = downloadStatusToTKeys.error
      notifyProgress({
        id: notificationKey,
        title: t(errorTKeys.title),
        description: t(errorTKeys.description),
        variant: 'error',
        progress: 100,
        closable: true,
      })
      setIsLoading(false)
    }
  }, [spreadSheetData, fileName, organizationName, notificationKey, t])

  return { exportAsXlsx, isLoading }
}
